import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyProductForm = {
  product_id: "",
  name: "",
  description: "",
  price: "",
  category: "magliette",
  collection: "astro",
  sizes: "S, M, L, XL",
  colors: "Nero, Bianco",
  image_url: "",
  images: "",
  in_stock: true,
};

const orderStatusOptions = [
  { value: "all", label: "Tutti" },
  { value: "pending", label: "In attesa" },
  { value: "paid", label: "Pagato" },
  { value: "processing", label: "In preparazione" },
  { value: "shipped", label: "Spedito" },
  { value: "delivered", label: "Consegnato" },
];

const orderStatusClass = {
  pending: "bg-gray-200",
  paid: "bg-accent-blue",
  processing: "bg-accent-yellow",
  shipped: "bg-accent-pink text-white",
  delivered: "bg-accent-green",
};

const toProductForm = (product) => ({
  product_id: product.product_id || "",
  name: product.name || "",
  description: product.description || "",
  price: product.price != null ? String(product.price) : "",
  category: product.category || "magliette",
  collection: product.collection || "astro",
  sizes: (product.sizes || []).join(", "),
  colors: (product.colors || []).join(", "),
  image_url: product.image_url || "",
  images: (product.images || []).join(", "),
  in_stock: product.in_stock !== false,
});

const parseList = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const formatOrderStatus = (status) => {
  const item = orderStatusOptions.find((s) => s.value === status);
  return item?.label || status;
};

const defaultOrderEdit = {
  status: "pending",
  tracking_number: "",
  admin_details: "",
};

const AdminPage = () => {
  const { user } = useAuth();
  const canEdit = useMemo(() => user?.role === "admin", [user]);

  const [activeSection, setActiveSection] = useState("products");

  const [products, setProducts] = useState([]);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSaving, setProductSaving] = useState(false);
  const [productError, setProductError] = useState("");
  const [productNotice, setProductNotice] = useState("");
  const [formPanelHeight, setFormPanelHeight] = useState(null);
  const formPanelRef = useRef(null);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState("all");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderEdit, setOrderEdit] = useState(defaultOrderEdit);
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [orderNotice, setOrderNotice] = useState("");

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductError("");
    try {
      const response = await axios.get(`${API}/admin/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (err) {
      setProductError("Impossibile caricare i prodotti");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadOrderDetail = useCallback(async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}`, { withCredentials: true });
      const order = response.data;
      setSelectedOrderId(order.order_id);
      setSelectedOrder(order);
      setOrderEdit({
        status: order.status || "pending",
        tracking_number: order.tracking_number || "",
        admin_details: order.admin_details || "",
      });
      setOrderError("");
    } catch (err) {
      setOrderError("Impossibile caricare il dettaglio ordine");
    }
  }, []);

  const fetchOrders = useCallback(async (status = orderFilter) => {
    setOrdersLoading(true);
    setOrderError("");
    try {
      const params = status && status !== "all" ? { status } : undefined;
      const response = await axios.get(`${API}/admin/orders`, { params, withCredentials: true });
      setOrders(response.data);

      if (response.data.length === 0) {
        setSelectedOrderId(null);
        setSelectedOrder(null);
        setOrderEdit(defaultOrderEdit);
        return;
      }

      const keepSelected = response.data.some((order) => order.order_id === selectedOrderId);
      const targetOrderId = keepSelected ? selectedOrderId : response.data[0].order_id;
      await loadOrderDetail(targetOrderId);
    } catch (err) {
      setOrderError("Impossibile caricare gli ordini");
    } finally {
      setOrdersLoading(false);
    }
  }, [loadOrderDetail, orderFilter, selectedOrderId]);

  useEffect(() => {
    if (!canEdit) return;
    if (activeSection === "products") {
      fetchProducts();
    } else {
      fetchOrders();
    }
  }, [activeSection, canEdit, fetchOrders, fetchProducts]);

  useEffect(() => {
    if (!canEdit || activeSection !== "orders") return;
    fetchOrders(orderFilter);
  }, [activeSection, canEdit, fetchOrders, orderFilter]);

  useEffect(() => {
    if (activeSection !== "products") return;
    const formPanel = formPanelRef.current;
    if (!formPanel) return;

    const updateHeight = () => setFormPanelHeight(formPanel.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(formPanel);

    return () => observer.disconnect();
  }, [activeSection, editingProductId, productError, productNotice]);

  const resetProductForm = () => {
    setEditingProductId(null);
    setProductForm(emptyProductForm);
    setProductNotice("");
    setProductError("");
  };

  const selectProduct = (product) => {
    setEditingProductId(product.product_id);
    setProductForm(toProductForm(product));
    setProductNotice("");
    setProductError("");
  };

  const updateProductForm = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setProductSaving(true);
    setProductError("");
    setProductNotice("");

    const price = Number(productForm.price);
    if (
      !productForm.name.trim() ||
      !productForm.description.trim() ||
      !productForm.image_url.trim() ||
      Number.isNaN(price)
    ) {
      setProductError("Compila tutti i campi obbligatori");
      setProductSaving(false);
      return;
    }

    const payload = {
      name: productForm.name.trim(),
      description: productForm.description.trim(),
      price,
      category: productForm.category,
      collection: productForm.collection,
      sizes: parseList(productForm.sizes),
      colors: parseList(productForm.colors),
      image_url: productForm.image_url.trim(),
      images: parseList(productForm.images),
      in_stock: productForm.in_stock,
    };

    try {
      if (editingProductId) {
        await axios.put(`${API}/admin/products/${editingProductId}`, payload, { withCredentials: true });
        setProductNotice("Prodotto aggiornato");
      } else {
        if (!productForm.product_id.trim()) {
          setProductError("Inserisci un product_id");
          setProductSaving(false);
          return;
        }
        await axios.post(
          `${API}/admin/products`,
          { ...payload, product_id: productForm.product_id.trim() },
          { withCredentials: true }
        );
        setProductNotice("Prodotto creato");
      }
      await fetchProducts();
      if (!editingProductId) {
        setProductForm(emptyProductForm);
      }
    } catch (err) {
      const message = err?.response?.data?.detail || "Operazione non riuscita";
      setProductError(message);
    } finally {
      setProductSaving(false);
    }
  };

  const removeProduct = async (productId) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    setProductSaving(true);
    setProductError("");
    setProductNotice("");
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
      setProductNotice("Prodotto eliminato");
      await fetchProducts();
      if (editingProductId === productId) {
        resetProductForm();
      }
    } catch (err) {
      const message = err?.response?.data?.detail || "Impossibile eliminare";
      setProductError(message);
    } finally {
      setProductSaving(false);
    }
  };

  const submitOrderUpdate = async (e) => {
    e.preventDefault();
    if (!selectedOrderId) return;

    setOrderSaving(true);
    setOrderError("");
    setOrderNotice("");

    try {
      const response = await axios.put(
        `${API}/admin/orders/${selectedOrderId}`,
        {
          status: orderEdit.status,
          tracking_number: orderEdit.tracking_number,
          admin_details: orderEdit.admin_details,
        },
        { withCredentials: true }
      );

      setSelectedOrder(response.data);
      setOrders((prev) =>
        prev.map((order) =>
          order.order_id === selectedOrderId
            ? {
                ...order,
                status: response.data.status,
                tracking_number: response.data.tracking_number,
                admin_details: response.data.admin_details,
              }
            : order
        )
      );
      setOrderNotice("Ordine aggiornato");
    } catch (err) {
      const message = err?.response?.data?.detail || "Impossibile aggiornare l'ordine";
      setOrderError(message);
    } finally {
      setOrderSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="border-4 border-black p-8 font-body text-xl">Accesso non autorizzato</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-10" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="font-heading text-4xl">Admin Dashboard</h1>
          <div className="flex gap-3">
            <button
              className={`btn-brutal-outline ${activeSection === "products" ? "bg-accent-yellow" : ""}`}
              onClick={() => setActiveSection("products")}
            >
              Prodotti
            </button>
            <button
              className={`btn-brutal-outline ${activeSection === "orders" ? "bg-accent-blue" : ""}`}
              onClick={() => setActiveSection("orders")}
            >
              Ordini
            </button>
          </div>
        </div>

        {activeSection === "products" ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-3xl">Gestione Prodotti</h2>
              <button className="btn-brutal-outline" onClick={resetProductForm}>
                Nuovo prodotto
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
              <div
                className="border-4 border-black p-4 flex flex-col min-h-0"
                style={formPanelHeight ? { height: `${formPanelHeight}px` } : undefined}
              >
                <h3 className="font-heading text-2xl mb-4">Catalogo</h3>
                {productsLoading ? (
                  <div className="font-mono">Caricamento...</div>
                ) : (
                  <div className="space-y-3 overflow-y-auto pr-2 flex-1 min-h-0">
                    {products.map((product) => (
                      <div
                        key={product.product_id}
                        className="border-2 border-black p-3 flex items-start justify-between gap-4"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-14 h-14 border-2 border-black object-cover flex-shrink-0 bg-gray-100"
                          />
                          <div className="min-w-0">
                            <div className="font-body font-semibold truncate">{product.name}</div>
                            <div className="font-mono text-xs truncate">{product.product_id}</div>
                            <div className="font-mono text-xs">EUR {product.price}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="btn-brutal-outline px-3 py-1 text-xs"
                            onClick={() => selectProduct(product)}
                          >
                            Modifica
                          </button>
                          <button
                            className="btn-brutal px-3 py-1 text-xs bg-red-600 border-red-600"
                            onClick={() => removeProduct(product.product_id)}
                          >
                            Elimina
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div ref={formPanelRef} className="border-4 border-black p-6">
                <h3 className="font-heading text-2xl mb-4">
                  {editingProductId ? "Modifica prodotto" : "Crea prodotto"}
                </h3>
                <form onSubmit={submitProduct} className="space-y-4">
                  <div>
                    <label className="block font-body font-semibold mb-2">Product ID</label>
                    <input
                      name="product_id"
                      value={productForm.product_id}
                      onChange={updateProductForm}
                      disabled={Boolean(editingProductId)}
                      className="w-full border-2 border-black p-3 font-body"
                      placeholder="astro-tshirt-3"
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Nome</label>
                    <input
                      name="name"
                      value={productForm.name}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Descrizione</label>
                    <textarea
                      name="description"
                      value={productForm.description}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body min-h-[120px]"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body font-semibold mb-2">Prezzo</label>
                      <input
                        name="price"
                        value={productForm.price}
                        onChange={updateProductForm}
                        className="w-full border-2 border-black p-3 font-body"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold mb-2">In stock</label>
                      <input
                        type="checkbox"
                        name="in_stock"
                        checked={productForm.in_stock}
                        onChange={updateProductForm}
                        className="w-5 h-5 border-2 border-black"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body font-semibold mb-2">Categoria</label>
                      <select
                        name="category"
                        value={productForm.category}
                        onChange={updateProductForm}
                        className="w-full border-2 border-black p-3 font-body"
                      >
                        <option value="magliette">Magliette</option>
                        <option value="felpe">Felpe</option>
                        <option value="homemade">Homemade</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-body font-semibold mb-2">Collezione</label>
                      <select
                        name="collection"
                        value={productForm.collection}
                        onChange={updateProductForm}
                        className="w-full border-2 border-black p-3 font-body"
                      >
                        <option value="astro">Astro</option>
                        <option value="drinks">Drinks</option>
                        <option value="quotes">Quotes</option>
                        <option value="handmade">Handmade</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Taglie (separate da virgola)</label>
                    <input
                      name="sizes"
                      value={productForm.sizes}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body"
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Colori (separati da virgola)</label>
                    <input
                      name="colors"
                      value={productForm.colors}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body"
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Immagine principale</label>
                    <input
                      name="image_url"
                      value={productForm.image_url}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold mb-2">Immagini extra (URL separati da virgola)</label>
                    <input
                      name="images"
                      value={productForm.images}
                      onChange={updateProductForm}
                      className="w-full border-2 border-black p-3 font-body"
                    />
                  </div>

                  {(productError || productNotice) && (
                    <div
                      className={`border-2 border-black p-3 font-mono text-sm ${
                        productError ? "bg-accent-yellow" : "bg-accent-blue"
                      }`}
                    >
                      {productError || productNotice}
                    </div>
                  )}

                  <button className="btn-brutal w-full" type="submit" disabled={productSaving}>
                    {productSaving ? "Salvataggio..." : editingProductId ? "Aggiorna" : "Crea"}
                  </button>
                </form>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-4 mb-6">
              <h2 className="font-heading text-3xl">Gestione Ordini</h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs uppercase text-gray-500 mr-2">Filtro stato:</span>
                {orderStatusOptions.map((statusItem) => (
                  <button
                    key={statusItem.value}
                    onClick={() => setOrderFilter(statusItem.value)}
                    className={`font-mono text-xs uppercase px-3 py-2 border-2 border-black ${
                      orderFilter === statusItem.value ? "bg-accent-yellow shadow-brutal" : "bg-white hover:bg-gray-100"
                    }`}
                  >
                    {statusItem.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.3fr] gap-8 items-start">
              <div className="border-4 border-black p-4">
                <h3 className="font-heading text-2xl mb-4">Ordini ricevuti</h3>
                {ordersLoading ? (
                  <div className="font-mono">Caricamento...</div>
                ) : orders.length === 0 ? (
                  <div className="border-2 border-black p-6 font-body text-gray-600">
                    Nessun ordine trovato per questo stato.
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                    {orders.map((order) => (
                      <button
                        key={order.order_id}
                        onClick={() => loadOrderDetail(order.order_id)}
                        className={`w-full text-left border-2 border-black p-3 ${
                          selectedOrderId === order.order_id ? "bg-accent-blue/20" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-mono text-xs truncate">#{order.order_id}</p>
                            <p className="font-body font-semibold truncate">{order.customer?.name || "Cliente"}</p>
                            <p className="font-mono text-xs text-gray-500 truncate">{order.customer?.email || order.user_id}</p>
                          </div>
                          <span
                            className={`font-mono text-xs uppercase px-2 py-1 border-2 border-black ${orderStatusClass[order.status] || "bg-gray-200"}`}
                          >
                            {formatOrderStatus(order.status)}
                          </span>
                        </div>
                        <div className="mt-2 font-mono text-xs text-gray-700 flex items-center justify-between">
                          <span>{order.items?.length || 0} articoli</span>
                          <span>EUR {Number(order.total || 0).toFixed(2)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-4 border-black p-6">
                {!selectedOrder ? (
                  <p className="font-body text-gray-600">Seleziona un ordine per vedere i dettagli.</p>
                ) : (
                  <>
                    <div className="mb-6">
                      <h3 className="font-heading text-2xl">Ordine #{selectedOrder.order_id}</h3>
                      <p className="font-mono text-sm text-gray-500 mt-1">
                        {new Date(selectedOrder.created_at).toLocaleString("it-IT")}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div className="border-2 border-black p-4">
                        <p className="font-mono text-xs uppercase text-gray-500 mb-2">Cliente</p>
                        <p className="font-body font-semibold">{selectedOrder.customer?.name || "N/A"}</p>
                        <p className="font-mono text-sm">{selectedOrder.customer?.email || "N/A"}</p>
                      </div>
                      <div className="border-2 border-black p-4">
                        <p className="font-mono text-xs uppercase text-gray-500 mb-2">Spedizione</p>
                        <p className="font-body text-sm">{selectedOrder.shipping_address?.full_name || "N/A"}</p>
                        <p className="font-body text-sm">{selectedOrder.shipping_address?.address || ""}</p>
                        <p className="font-body text-sm">
                          {selectedOrder.shipping_address?.postal_code || ""} {selectedOrder.shipping_address?.city || ""}
                        </p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-heading text-xl mb-3">Articoli ordine</h4>
                      <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                        {(selectedOrder.items || []).map((item, idx) => (
                          <div key={idx} className="border-2 border-black p-3 flex items-center gap-3">
                            <img
                              src={item.product?.image_url}
                              alt={item.product?.name || "Prodotto"}
                              className="w-14 h-14 border-2 border-black object-cover bg-gray-100"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-body font-semibold truncate">{item.product?.name || item.product_id}</p>
                              <p className="font-mono text-xs">{item.size} / {item.color} - Qta {item.quantity}</p>
                            </div>
                            <p className="font-mono text-sm">EUR {Number(item.subtotal || 0).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <form onSubmit={submitOrderUpdate} className="space-y-4 border-t-2 border-black pt-6">
                      <h4 className="font-heading text-xl">Aggiorna ordine</h4>
                      <div>
                        <label className="block font-body font-semibold mb-2">Stato ordine</label>
                        <select
                          value={orderEdit.status}
                          onChange={(e) => setOrderEdit((prev) => ({ ...prev, status: e.target.value }))}
                          className="w-full border-2 border-black p-3 font-body"
                        >
                          {orderStatusOptions
                            .filter((item) => item.value !== "all")
                            .map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block font-body font-semibold mb-2">Tracking</label>
                        <input
                          value={orderEdit.tracking_number}
                          onChange={(e) => setOrderEdit((prev) => ({ ...prev, tracking_number: e.target.value }))}
                          className="w-full border-2 border-black p-3 font-body"
                          placeholder="Es. RK12345678"
                        />
                      </div>
                      <div>
                        <label className="block font-body font-semibold mb-2">Dettagli aggiuntivi (admin)</label>
                        <textarea
                          value={orderEdit.admin_details}
                          onChange={(e) => setOrderEdit((prev) => ({ ...prev, admin_details: e.target.value }))}
                          className="w-full border-2 border-black p-3 font-body min-h-[120px]"
                          placeholder="Note su spedizione, lavorazione, comunicazioni cliente..."
                        />
                      </div>

                      {(orderError || orderNotice) && (
                        <div
                          className={`border-2 border-black p-3 font-mono text-sm ${
                            orderError ? "bg-accent-yellow" : "bg-accent-blue"
                          }`}
                        >
                          {orderError || orderNotice}
                        </div>
                      )}

                      <button className="btn-brutal w-full" type="submit" disabled={orderSaving}>
                        {orderSaving ? "Aggiornamento..." : "Salva aggiornamenti ordine"}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
